/**
 * WellnessCheckIn — the daily check-in, answered from home.
 * This renders the SAME questions the gym uses at the attendance kiosk (the
 * unified check-in system) and submits to checkin_responses; the backend derives
 * the 0-100 readiness score from the questions tagged as readiness contributors.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { useAthletes } from '../hooks/useAthletes.js';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import BodyMapInput from '../components/BodyMapInput.jsx';
import { FACE_SCALE } from '../utils/bodyRegions.js';

const readinessColor = (s) => s == null ? '#9ca3af' : s >= 70 ? '#16a34a' : s >= 45 ? '#d97706' : '#dc2626';

export default function WellnessCheckIn() {
  const { user } = useAuth();
  const { athletes } = useAthletes();
  const isAthlete = user?.role === 'athlete';

  const [athleteId, setAthleteId] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Resolve who we're checking in for: athletes resolve their own record; coaches
  // /parents pick from their athlete list.
  useEffect(() => {
    let alive = true;
    if (isAthlete) {
      api.get(`${API_BASE_URL}/api/athlete/me`)
        .then(r => { if (alive) setAthleteId(r.data?.id ? String(r.data.id) : ''); })
        .catch(() => { if (alive) setError('Could not find your athlete profile.'); });
    } else if (!athleteId && athletes.length) {
      setAthleteId(String(athletes[0].id));
    }
    return () => { alive = false; };
  }, [isAthlete, athletes]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!athleteId) { setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.get(`${API_BASE_URL}/api/checkin/my-questions?athlete_id=${athleteId}`);
      setQuestions(data.questions || []);
      setAnswers(data.today || {});
      setReadiness(data.readiness ?? null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your check-in.');
      setQuestions([]);
    } finally { setLoading(false); }
  }, [athleteId]);
  useEffect(() => { load(); }, [load]);

  const setAnswer = (qid, val) => setAnswers(a => ({ ...a, [qid]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!athleteId) { setError('Pick an athlete.'); return; }
    setSubmitting(true);
    try {
      const payload = questions
        .filter(q => answers[q.id] != null && answers[q.id] !== '')
        .map(q => ({ question_id: q.id, response: answers[q.id] }));
      const { data } = await api.post(`${API_BASE_URL}/api/checkin/self-responses`, {
        athlete_id: Number(athleteId), answers: payload,
      });
      setReadiness(data.readiness ?? null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save check-in');
    } finally { setSubmitting(false); }
  };

  const box = { display: 'block', marginBottom: 18 };
  const btn = (on) => ({
    flex: 1, padding: '10px 0', borderRadius: 8, fontSize: '.9rem', fontWeight: 700, cursor: 'pointer',
    border: on ? '1px solid #4f46e5' : '1px solid #d1d5db', background: on ? '#4f46e5' : '#fff', color: on ? '#fff' : '#475569',
  });

  const renderQuestion = (q) => {
    const val = answers[q.id];
    if (q.qtype === 'faces') {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          {FACE_SCALE.map(f => (
            <button key={f.v} type="button" onClick={() => setAnswer(q.id, f.v)}
              title={f.label}
              style={{ ...btn(Number(val) === f.v), fontSize: '1.5rem', padding: '6px 0' }}>{f.face}</button>
          ))}
        </div>
      );
    }
    if (q.qtype === 'scale') {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => setAnswer(q.id, n)} style={btn(Number(val) === n)}>{n}</button>
          ))}
        </div>
      );
    }
    if (q.qtype === 'yesno') {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          {['Yes', 'No'].map(o => (
            <button key={o} type="button" onClick={() => setAnswer(q.id, o)} style={btn(val === o)}>{o}</button>
          ))}
        </div>
      );
    }
    if (q.qtype === 'bodymap') {
      return <BodyMapInput value={val || ''} onChange={(arr) => setAnswer(q.id, arr.join(','))} />;
    }
    return (
      <textarea value={val || ''} onChange={e => setAnswer(q.id, e.target.value)} rows={2}
        placeholder="Anything your coach should know…"
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '.9rem' }} />
    );
  };

  return (
    <div style={{ padding: '1rem', maxWidth: 560, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#111827', margin: 0 }}>✅ Daily Check-In</h1>
      <p style={{ fontSize: '.9rem', color: '#6b7280', margin: '4px 0 18px' }}>
        A few quick taps. Your coaches use this to manage load and catch problems early.
      </p>

      {!isAthlete && (
        <label style={box}>
          <span style={{ fontSize: '.85rem', fontWeight: 600, color: '#374151' }}>Athlete</span>
          <select value={athleteId} onChange={e => setAthleteId(e.target.value)}
            style={{ marginTop: 4, width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '.9rem' }}>
            <option value="">Select…</option>
            {athletes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
      )}

      {loading ? (
        <p style={{ color: '#9ca3af' }}>Loading…</p>
      ) : questions.length === 0 ? (
        <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: '1.25rem', color: '#64748b', fontSize: '.9rem' }}>
          No check-in questions are set for you right now. {!isAthlete && 'Add them under Operations → Classes & Attendance → ⚙️ Check-in questions.'}
        </div>
      ) : (
        <form onSubmit={submit}>
          {questions.map(q => (
            <div key={q.id} style={box}>
              <div style={{ fontSize: '.9rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {q.prompt}{q.readiness_role && <span style={{ color: '#a5b4fc', fontWeight: 400 }}> · 🎯</span>}
              </div>
              {renderQuestion(q)}
            </div>
          ))}

          {error && <p style={{ color: '#dc2626', fontSize: '.85rem' }}>{error}</p>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 6 }}>
            <button type="submit" disabled={submitting}
              style={{ padding: '10px 20px', borderRadius: 8, background: '#4f46e5', color: '#fff', fontWeight: 700, border: 'none', cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Saving…' : 'Submit Check-In'}
            </button>
            {readiness != null && (
              <div style={{ fontSize: '.85rem', color: '#6b7280' }}>
                Readiness: <span style={{ fontSize: '1.6rem', fontWeight: 800, color: readinessColor(readiness) }}>{readiness}</span>/100
              </div>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
